import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import { cls } from "../hatim/soft/SoftKit";
import { useToast } from "../../components/ui/Toast";
import {
  useListLibraryQuery,
  useGetLibraryPackageQuery,
  useVerifyPackageMutation,
  useRecommendPackageMutation,
  useDeletePackageMutation,
} from "../../api/adminApi";
import { formatDate, formatNumber, correctAnswerText, QUESTION_TYPE_LABELS } from "../../lib/adminFormat";
import {
  PageHeader,
  Section,
  NoData,
  ErrorRetry,
  SkeletonRows,
  Pager,
  ConfirmDialog,
  Tag,
  SearchInput,
  TabStrip,
  useDebouncedValue,
} from "./components/AdminKit";

const TABS = [
  { value: "pending", label: "Onay bekleyenler" },
  { value: "verified", label: "Onaylı" },
  { value: "recommended", label: "Tavsiyeli" },
  { value: "hidden", label: "Gizlenen" },
];
const TAB_VALUES = TABS.map((t) => t.value);

function PackageTags({ pkg }) {
  return (
    <>
      {pkg.verified && <Tag tone="ok">Onaylı</Tag>}
      {pkg.recommended && <Tag tone="brand">Tavsiyeli</Tag>}
      {pkg.hidden && <Tag tone="danger">Gizli</Tag>}
      {pkg.reportCount > 0 && <Tag tone="warn">{pkg.reportCount} bildirim</Tag>}
    </>
  );
}

function PackageList({ tab, onTab, q, onQ, selectedId, onSelect }) {
  const [page, setPage] = useState(1);
  const debouncedQ = useDebouncedValue(q.trim());
  useEffect(() => setPage(1), [tab, debouncedQ]);

  const { data, isLoading, isFetching, isError, error, refetch } = useListLibraryQuery({
    tab,
    q: debouncedQ,
    page,
  });
  const packages = data?.packages || [];
  const counts = data?.counts || {};

  return (
    <Section bodyClassName="p-0 flex flex-col min-h-0" className="min-h-0">
      <div className="px-2">
        <TabStrip
          tabs={TABS.map((t) => ({ ...t, count: counts[t.value] }))}
          value={tab}
          onChange={onTab}
        />
      </div>
      <div className={`px-4 py-3 ${cls.row}`}>
        <SearchInput value={q} onChange={onQ} placeholder="Başlık, açıklama ya da etiket ara" className="w-full" />
      </div>
      {isLoading ? (
        <SkeletonRows count={6} />
      ) : isError ? (
        <ErrorRetry message={error?.message} onRetry={refetch} error={error} />
      ) : packages.length === 0 ? (
        <NoData text={debouncedQ ? "Aramaya uyan paket yok" : "Bu sekmede paket yok"} />
      ) : (
        <ul className={`min-h-0 lg:overflow-y-auto transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          {packages.map((pkg) => {
            const active = pkg.id === selectedId;
            return (
              <li key={pkg.id}>
                <button
                  type="button"
                  onClick={() => onSelect(pkg.id)}
                  className={`w-full text-left px-4 py-3 flex flex-col gap-1 ${cls.row} ${cls.rowHover} ${
                    active ? "bg-terakkiBrandCloud dark:bg-white/[0.08]" : ""
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`text-base truncate ${cls.title}`}>{pkg.title}</span>
                    <PackageTags pkg={pkg} />
                  </span>
                  <span className={`text-sm ${cls.muted}`}>
                    {pkg.ownerName || pkg.ownerEmail || "Bilinmeyen sahip"} · {formatNumber(pkg.questionCount)} soru ·{" "}
                    {formatDate(pkg.updatedAt)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Pager page={page} hasMore={Boolean(data?.hasMore)} onChange={setPage} busy={isFetching} />
    </Section>
  );
}

function QuestionItem({ question, index }) {
  const answer = correctAnswerText(question);
  return (
    <li className={`px-4 py-3 ${cls.row} last:border-b-0`}>
      <div className="flex items-start gap-3">
        <span className={`w-6 shrink-0 text-sm tabular-nums pt-0.5 ${cls.faint}`}>{index + 1}.</span>
        <div className="min-w-0 flex-1">
          <p className={`text-base leading-snug ${cls.title}`}>{question.text}</p>
          <p className={`text-xs mt-0.5 ${cls.faint}`}>{QUESTION_TYPE_LABELS[question.type] || question.type}</p>
          {question.type === "coktan" && Array.isArray(question.options) ? (
            <ul className="mt-2 flex flex-col gap-1">
              {question.options.map((opt, i) => {
                const correct = i === question.correctIndex;
                return (
                  <li
                    key={i}
                    className={`flex items-center gap-2 text-sm ${
                      correct ? "text-terakkiSuccess dark:text-[#7BE3A8]" : cls.muted
                    }`}
                  >
                    <span className="w-4 shrink-0 flex justify-center">
                      {correct ? <CheckIcon sx={{ fontSize: 16 }} aria-label="Doğru cevap" /> : "·"}
                    </span>
                    {opt}
                  </li>
                );
              })}
            </ul>
          ) : question.type === "acik" ? (
            <p className={`mt-1.5 text-sm ${cls.muted}`}>Açık uçlu, doğru cevap yok</p>
          ) : (
            answer && (
              <p className="mt-1.5 text-sm text-terakkiSuccess dark:text-[#7BE3A8]">Doğru cevap: {answer}</p>
            )
          )}
        </div>
      </div>
    </li>
  );
}

function PackageDetail({ packageId, onBack, onDeleted }) {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useGetLibraryPackageQuery({ packageId });
  const [verify, verifyState] = useVerifyPackageMutation();
  const [recommend, recommendState] = useRecommendPackageMutation();
  const [remove, removeState] = useDeletePackageMutation();
  const [confirm, setConfirm] = useState(null);
  const pkg = data?.package;
  const busy = verifyState.isLoading || recommendState.isLoading || removeState.isLoading;

  const run = async (action) => {
    let result;
    let okText;
    if (action === "verify" || action === "unverify") {
      result = await verify({ packageId, verified: action === "verify" });
      okText = action === "verify" ? "Paket onaylandı." : "Onay kaldırıldı.";
    } else if (action === "recommend" || action === "unrecommend") {
      result = await recommend({ packageId, recommended: action === "recommend" });
      okText = action === "recommend" ? "Paket tavsiye edildi." : "Tavsiye kaldırıldı.";
    } else if (action === "delete") {
      result = await remove({ packageId });
      okText = "Paket silindi.";
    }
    setConfirm(null);
    if (result?.error) {
      toast.error(result.error.message || "İşlem yapılamadı.");
      return;
    }
    toast.success(okText);
    if (action === "delete") onDeleted();
  };

  const confirmTexts = {
    verify: { title: "Paket onaylansın mı?", text: "Onaylı paketler kütüphanede herkese onaylı olarak görünür.", label: "Onayla" },
    unverify: { title: "Onay kaldırılsın mı?", text: "Paket yeniden onay bekleyenlere düşebilir.", label: "Onayı kaldır" },
    recommend: { title: "Paket tavsiye edilsin mi?", text: "Tavsiyeli paketler kütüphanede öne çıkar.", label: "Tavsiye et" },
    unrecommend: { title: "Tavsiye kaldırılsın mı?", text: null, label: "Tavsiyeyi kaldır" },
    delete: {
      title: "Paket silinsin mi?",
      text: "Paket kütüphaneden kaldırılır ve açık önerileri geri çekilir. Sahibine bildirim gitmez.",
      label: "Sil",
      danger: true,
    },
  };

  const btn =
    "inline-flex items-center h-9 px-3.5 rounded-full text-sm border transition-colors disabled:opacity-50";
  const primaryBtn = `${btn} bg-terakkiPrimaryDark border-terakkiPrimaryDark text-white hover:bg-terakkiPrimary dark:bg-[#8FC9CA] dark:border-[#8FC9CA] dark:text-[#123B3D]`;
  const ghostBtn = `${btn} border-terakkiBorder text-terakkiPrimaryDark hover:bg-terakkiBrandCloud dark:border-white/[0.14] dark:text-[#8FC9CA] dark:hover:bg-white/[0.08]`;
  const dangerBtn = `${btn} border-terakkiError/40 text-terakkiError hover:bg-terakkiError/10 dark:border-[#F2A59C]/40 dark:text-[#F2A59C]`;

  const back = (
    <button type="button" onClick={onBack} className={`lg:hidden inline-flex items-center gap-1 text-sm ${cls.accent}`}>
      <ArrowBackIcon sx={{ fontSize: 16 }} aria-hidden /> Listeye dön
    </button>
  );

  if (isLoading) {
    return (
      <Section bodyClassName="p-0">
        <div className="p-4">{back}</div>
        <SkeletonRows count={5} />
      </Section>
    );
  }
  if (isError || !pkg) {
    return (
      <Section bodyClassName="p-0">
        <div className="p-4">{back}</div>
        <ErrorRetry message={error?.message || "Paket bulunamadı."} onRetry={refetch} error={error} />
      </Section>
    );
  }

  const questions = pkg.questions || [];
  const c = confirm ? confirmTexts[confirm] : null;

  return (
    <Section bodyClassName="p-0 flex flex-col min-h-0" className="min-h-0">
      <div className={`px-4 py-4 flex flex-col gap-2 ${cls.summary}`}>
        {back}
        <div className="flex items-start gap-2 flex-wrap">
          <h2 className={`text-xl font-Medium leading-snug min-w-0 ${cls.title}`}>{pkg.title}</h2>
          <PackageTags pkg={pkg} />
        </div>
        {pkg.description && <p className={`text-base ${cls.muted}`}>{pkg.description}</p>}
        <p className={`text-sm ${cls.muted}`}>
          {pkg.ownerName || "Bilinmeyen sahip"}
          {pkg.ownerEmail ? ` · ${pkg.ownerEmail}` : ""} · sürüm {pkg.version || 1} · {formatDate(pkg.updatedAt)}
        </p>
        <p className={`text-sm ${cls.faint}`}>
          {formatNumber(pkg.starCount)} yıldız · {formatNumber(pkg.forkCount)} çatal · {formatNumber(pkg.useCount)} kullanım
          {pkg.forkedFrom?.title ? ` · "${pkg.forkedFrom.title}" paketinden çatal` : ""}
        </p>
        {Array.isArray(pkg.tags) && pkg.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pkg.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {pkg.hidden ? (
            <button type="button" className={dangerBtn} disabled={busy} onClick={() => setConfirm("delete")}>
              Sil
            </button>
          ) : (
            <>
              {pkg.verified ? (
                <button type="button" className={ghostBtn} disabled={busy} onClick={() => setConfirm("unverify")}>
                  Onayı kaldır
                </button>
              ) : (
                <button type="button" className={primaryBtn} disabled={busy} onClick={() => setConfirm("verify")}>
                  Onayla
                </button>
              )}
              {pkg.recommended ? (
                <button type="button" className={ghostBtn} disabled={busy} onClick={() => setConfirm("unrecommend")}>
                  Tavsiyeyi kaldır
                </button>
              ) : (
                <button type="button" className={ghostBtn} disabled={busy} onClick={() => setConfirm("recommend")}>
                  Tavsiye et
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className={`px-4 py-2.5 text-sm ${cls.muted} ${cls.row}`}>{formatNumber(questions.length)} soru</div>
      {questions.length === 0 ? (
        <NoData text="Pakette soru yok" />
      ) : (
        <ol className="min-h-0 lg:overflow-y-auto">
          {questions.map((q, i) => (
            <QuestionItem key={q.qid || i} question={q} index={i} />
          ))}
        </ol>
      )}
      <ConfirmDialog
        open={Boolean(c)}
        title={c?.title}
        text={c?.text}
        confirmLabel={c?.label}
        danger={c?.danger}
        busy={busy}
        onConfirm={() => run(confirm)}
        onClose={() => setConfirm(null)}
      />
    </Section>
  );
}

export default function LibraryReviewPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TAB_VALUES.includes(params.get("sekme")) ? params.get("sekme") : "pending";
  const [q, setQ] = useState("");

  const search = tab === "pending" ? "" : `?sekme=${tab}`;
  const setTab = (next) => {
    setParams(next === "pending" ? {} : { sekme: next }, { replace: true });
  };
  const select = (id) => navigate(`/sistem/kutuphane/${id}${search}`);
  const backToList = () => navigate(`/sistem/kutuphane${search}`);

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-5 pb-8 lg:h-full">
      <PageHeader title="Kütüphane Denetimi" subtitle="Müzakere kütüphanesindeki paketlerin onayı ve tavsiyesi" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start lg:flex-1 lg:min-h-0">
        <div className={`${packageId ? "hidden lg:flex" : "flex"} flex-col lg:col-span-2 lg:max-h-[calc(100vh-150px)] min-h-0`}>
          <PackageList
            tab={tab}
            onTab={setTab}
            q={q}
            onQ={setQ}
            selectedId={packageId}
            onSelect={select}
          />
        </div>
        <div className={`${packageId ? "flex" : "hidden lg:flex"} flex-col lg:col-span-3 lg:max-h-[calc(100vh-150px)] min-h-0`}>
          {packageId ? (
            <PackageDetail key={packageId} packageId={packageId} onBack={backToList} onDeleted={backToList} />
          ) : (
            <Section bodyClassName="p-0">
              <div className="py-16 flex flex-col items-center gap-2 text-center px-6">
                <LibraryBooksOutlinedIcon sx={{ fontSize: 32 }} className="text-terakkiBorder dark:text-white/30" aria-hidden />
                <p className={`text-base ${cls.muted}`}>Ayrıntısını görmek için soldan bir paket seçin.</p>
                <Link to="/sistem/pano" className={`text-sm ${cls.accent} hover:underline`}>
                  Panoya dön
                </Link>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
